import { z } from "zod";

const Coordinate = z.tuple([z.number(), z.number()]);

export const Citation = z.object({
  id: z.string(),
  documentId: z.string(),
  encodedCitationString: z.string(),
  shortSummary: z.string().nullable(),
  boundingBox: z
    .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
    .nullable(),
  pageNumber: z.number().nullable(),
  pageWidth: z.number().nullable(),
  pageHeight: z.number().nullable(),
  citationOrder: z.number().nullable(),
  reperformanceStepId: z.string().nullable().optional(),
  attributeTestResultId: z.string().nullable().optional(),
  outcomeSpanStart: z.number().nullable().optional(),
  outcomeSpanEnd: z.number().nullable().optional(),
  isKey: z.boolean().nullable().optional(),
  citationGroupId: z.string().nullable().optional(),
  // Auditor-trail (Evidence Walk V3) parent — set on citations attached to an
  // L2 step; carried back in the write body so the API keeps the parent linkage.
  auditorStepId: z.string().nullable().optional(),
  // Video-specific: timestamp in seconds for video citations
  timestampSeconds: z.number().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Citation = z.infer<typeof Citation>;

/**
 * Parses serialised citation string into document_id and element_id.
 */
export function deserialiseCitationFromString(
  citationString: string,
): [string, string] {
  const parts = citationString.split("#");

  switch (parts[0]) {
    case "pdf-element":
      if (parts.length === 3) {
        const [, documentId, elementId] = parts;
        return [documentId, elementId];
      }
      break;

    case "pdf-table-cell":
      if (parts.length === 5) {
        const [, documentId, tableElementId, rowIndex, columnIndex] = parts;
        return [
          documentId,
          `${tableElementId}#cell_${rowIndex}_${columnIndex}`,
        ];
      }
      break;

    case "excel":
      if (parts.length === 4) {
        const [, documentId, sheetName, cellAddress] = parts;
        return [documentId, `'${sheetName}'!${cellAddress}`];
      }
      break;

    case "pdf-manual":
      if (parts.length === 2) {
        const [, documentId] = parts;
        return [documentId, "manual"];
      }
      break;

    case "video":
      if (parts.length >= 3) {
        const [, documentId, elementId] = parts;
        return [documentId, elementId];
      }
      break;

    default:
      return ["", "manual"];
  }

  throw new Error(`Invalid citation string: ${citationString}`);
}

/**
 * Extracts timestamp in seconds from a video citation string.
 * Supports two formats:
 * - 3 parts: video#{documentId}#{timestampSeconds}
 * - 4 parts: video#{documentId}#{elementId}#{timestampSeconds}
 * Returns undefined if not a video citation or timestamp not present.
 */
export function extractTimestampFromCitationString(
  citationString: string,
): number | undefined {
  const parts = citationString.split("#");

  if (parts[0] !== "video") {
    return undefined;
  }

  // Try 4-part format first: video#docId#elementId#timestamp
  if (parts.length >= 4) {
    const timestamp = parseFloat(parts[3]);
    if (!isNaN(timestamp)) {
      return timestamp;
    }
  }

  // Try 3-part format: video#docId#timestamp
  if (parts.length >= 3) {
    const timestamp = parseFloat(parts[2]);
    if (!isNaN(timestamp)) {
      return timestamp;
    }
  }

  return undefined;
}
